import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { hubAPI } from '../services/hubApi';
import { programAPI } from '../services/programApi';
import { Hub } from '../types/hub';
import { Program } from '../types/program';
import CatalogFacetSidebar from '../components/resources/CatalogFacetSidebar';
import CatalogResourceRow from '../components/resources/CatalogResourceRow';
import PlaceLink from '../components/layout/PlaceLink';
import Pagination from '../components/layout/Pagination';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import { placePath } from '../utils/placePaths';
import EmptyState from '../components/ui/EmptyState';
import {
  CATALOG_FACET_KEYS,
  EMPTY_CATALOG_FACETS,
  type CatalogFacetKey,
  type CatalogFacets,
  type CatalogListResponse,
  type CatalogResource,
} from '../types/resourceCatalog';
import { catalogLabel } from '../utils/catalogLabels';
import { hubPlaceHref } from '../utils/hubChapter';
import {
  libraryDoorHubs,
  libraryDoorPrograms,
  matchingPlaces,
  programDocumentIds,
  rowPlaceIndex,
} from '../utils/libraryPlaces';

const PER_PAGE = 20;
type CatalogSort = 'updated' | 'newest' | 'relevance';

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(',').map((value) => value.trim()).filter(Boolean);
}

function selectedFromParams(params: URLSearchParams): Record<CatalogFacetKey, string[]> {
  return {
    contentType: readList(params, 'contentType'),
    topic: readList(params, 'topic'),
    audience: readList(params, 'audience'),
    clinicalArea: readList(params, 'clinicalArea'),
    format: readList(params, 'format'),
  };
}

function effectiveSort(params: URLSearchParams): CatalogSort {
  const query = params.get('q')?.trim();
  const sort = params.get('sort');
  if (sort === 'newest') return 'newest';
  if (sort === 'updated') return 'updated';
  if (sort === 'relevance' && query) return 'relevance';
  return query ? 'relevance' : 'updated';
}

function catalogSearchParams(params: URLSearchParams, wpIds: number[]): string {
  const next = new URLSearchParams();
  const query = params.get('q')?.trim();
  if (query) next.set('q', query);
  for (const key of CATALOG_FACET_KEYS) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  const sort = effectiveSort(params);
  if (sort !== 'updated') next.set('sort', sort);
  if (wpIds.length > 0) next.set('wpIds', wpIds.join(','));
  const page = Number(params.get('page') || '1');
  if (Number.isFinite(page) && page > 1) next.set('page', String(page));
  next.set('perPage', String(PER_PAGE));
  return next.toString();
}

function DoorLink({
  kind,
  title,
  href,
  slug,
  placeKind,
}: {
  kind: 'program' | 'hub';
  title: string;
  href: string;
  slug: string;
  placeKind?: string;
}) {
  const label = kind === 'hub' ? 'Toolkit' : placeKind === 'REFERENCE' ? 'Reference' : 'Program';
  return (
    <PlaceLink
      to={href}
      surface={{ kind, slug, via: 'link' }}
      ariaLabel={`${label}: ${title}`}
      className="inline-flex flex-col justify-center min-h-[44px] px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-primary focus-ring"
    >
      <span className="text-xs font-semibold text-primary">{label}</span>
      <span className="text-sm font-medium text-gray-900">{title}</span>
    </PlaceLink>
  );
}

export default function ResourceLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [placesReady, setPlacesReady] = useState(false);
  const [placesFailed, setPlacesFailed] = useState(false);
  const [resources, setResources] = useState<CatalogResource[]>([]);
  const [facets, setFacets] = useState<CatalogFacets>(EMPTY_CATALOG_FACETS);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');

  const queryKey = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(queryKey), [queryKey]);
  const selected = useMemo(() => selectedFromParams(params), [params]);
  const selectedPrograms = useMemo(() => readList(params, 'program'), [params]);
  const query = params.get('q')?.trim() ?? '';
  const sort = effectiveSort(params);
  const currentPage = Math.max(1, Number(params.get('page') || '1') || 1);
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  useEffect(() => {
    setSearchInput(new URLSearchParams(queryKey).get('q') ?? '');
  }, [queryKey]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([programAPI.fetchPrograms(), hubAPI.fetchHubs()])
      .then(([programRows, hubRows]) => {
        if (cancelled) return;
        setPlacesFailed(false);
        setPrograms(programRows);
        setHubs(hubRows);
      })
      .catch(() => {
        if (!cancelled) setPlacesFailed(true);
      })
      .finally(() => {
        if (!cancelled) setPlacesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const doorPrograms = useMemo(() => libraryDoorPrograms(programs), [programs]);
  const doorHubs = useMemo(() => libraryDoorHubs(hubs), [hubs]);
  const placeMatches = useMemo(() => matchingPlaces(query, programs, hubs), [query, programs, hubs]);
  const startHere = placeMatches[0];
  const placesByPost = useMemo(() => rowPlaceIndex(hubs, programs), [hubs, programs]);
  const programOptions = useMemo(() => {
    const options = doorPrograms.map((program) => ({
      slug: program.slug,
      title: program.title,
      count: programDocumentIds(program).length,
    }));
    if (!placesReady) return options;
    for (const slug of selectedPrograms) {
      if (!options.some((option) => option.slug === slug)) {
        options.push({ slug, title: slug, count: 0 });
      }
    }
    return options;
  }, [doorPrograms, placesReady, selectedPrograms]);

  const programWpIds = useMemo(() => {
    if (selectedPrograms.length === 0 || !placesReady || placesFailed) return [];
    return [...new Set(selectedPrograms.flatMap((slug) => {
      const program = programs.find((item) => item.slug === slug);
      return program ? programDocumentIds(program) : [];
    }))];
  }, [selectedPrograms, placesReady, placesFailed, programs]);

  const catalogRequest = useMemo(() => {
    if (selectedPrograms.length > 0 && !placesReady) return null;
    if (selectedPrograms.length > 0 && placesFailed) return 'places-failed';
    if (selectedPrograms.length > 0 && programWpIds.length === 0) return 'empty-program';
    return catalogSearchParams(params, programWpIds);
  }, [params, placesFailed, placesReady, programWpIds, selectedPrograms.length]);

  useEffect(() => {
    if (!catalogRequest) return;
    if (catalogRequest === 'places-failed') {
      setLoading(false);
      setResources([]);
      setTotal(0);
      setError(new Error('Programs could not be loaded'));
      return;
    }
    if (catalogRequest === 'empty-program') {
      setLoading(false);
      setError(null);
      setResources([]);
      setFacets(EMPTY_CATALOG_FACETS);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`/api/tools/resources?${catalogRequest}`, { signal: controller.signal })
      .then(async (response) => {
        const body = await response.json() as CatalogListResponse | { success?: false; error?: string };
        if (!response.ok || !body || body.success !== true) {
          const message = body && 'error' in body && body.error
            ? body.error
            : 'Failed to load resources';
          throw new Error(message);
        }
        return body;
      })
      .then((body) => {
        if (controller.signal.aborted) return;
        setResources(body.data);
        setFacets(body.meta.facets ?? EMPTY_CATALOG_FACETS);
        setTotal(body.meta.total ?? 0);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setResources([]);
        setError(err instanceof Error ? err : new Error('Failed to load resources'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [catalogRequest]);

  const writeSearch = useMemo(
    () => debounce((value: string) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        const trimmed = value.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        if (!trimmed && next.get('sort') === 'relevance') next.delete('sort');
        next.delete('page');
        return next;
      });
    }, 300),
    [setSearchParams]
  );

  useEffect(() => () => writeSearch.cancel(), [writeSearch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    writeSearch(value);
  };

  const handleToggle = (facet: CatalogFacetKey, value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      const values = readList(next, facet);
      const updated = values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value];
      if (updated.length > 0) next.set(facet, updated.join(','));
      else next.delete(facet);
      next.delete('page');
      return next;
    });
  };

  const handleToggleProgram = (slug: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      const values = readList(next, 'program');
      const updated = values.includes(slug)
        ? values.filter((item) => item !== slug)
        : [...values, slug];
      if (updated.length > 0) next.set('program', updated.join(','));
      else next.delete('program');
      next.delete('page');
      return next;
    });
  };

  const handleSort = (value: string) => {
    const nextSort: CatalogSort = value === 'newest' || value === 'relevance' ? value : 'updated';
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      const hasQuery = Boolean(next.get('q')?.trim());
      const defaultSort: CatalogSort = hasQuery ? 'relevance' : 'updated';
      if (nextSort === defaultSort) next.delete('sort');
      else next.set('sort', nextSort);
      next.delete('page');
      return next;
    });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    writeSearch.cancel();
    setSearchParams({});
  };

  const handlePageChange = (page: number) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filterNames = [
    ...selectedPrograms.map((slug) => programOptions.find((program) => program.slug === slug)?.title ?? slug),
    ...CATALOG_FACET_KEYS.flatMap((key) => selected[key].map((value) => catalogLabel(value))),
  ];
  const emptyDescription = query || filterNames.length > 0
    ? `Nothing matched ${[query ? `“${query}”` : '', filterNames.join(', ')].filter(Boolean).join(' with ')}.`
    : 'Try a search or choose a filter.';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Resource Library
        </h1>
        <p className="text-gray-600">
          Browse our comprehensive collection of resources
        </p>
      </div>

      <div className="mb-8 max-w-2xl">
        <label htmlFor="resource-catalog-search" className="block text-sm font-medium text-gray-700 mb-2">
          Search
        </label>
        <Input
          id="resource-catalog-search"
          type="search"
          placeholder="Massive hemorrhage protocol, IVIG, bedside audit"
          value={searchInput}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
      </div>

      {(doorPrograms.length > 0 || doorHubs.length > 0) && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Programs</h2>
            <Link to="/resources" className="text-sm text-primary hover:text-primary-dark font-medium">
              View all
            </Link>
          </div>
          {doorPrograms.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {doorPrograms.map((program) => (
                <DoorLink
                  key={program.id}
                  kind="program"
                  title={program.title}
                  href={placePath(program)}
                  slug={program.slug}
                  placeKind={program.kind}
                />
              ))}
            </div>
          )}
          {doorHubs.length > 0 && (
            <div className={doorPrograms.length > 0 ? 'flex flex-wrap gap-2 mt-3' : 'flex flex-wrap gap-2'}>
              {doorHubs.map((hub) => (
                <DoorLink
                  key={hub.id}
                  kind="hub"
                  title={hub.title}
                  href={hubPlaceHref(hub)}
                  slug={hub.slug}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {startHere && (
        <Card className="!p-4 mb-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Start here</p>
          <PlaceLink
            to={startHere.href}
            surface={{ kind: startHere.kind, slug: startHere.slug, via: 'link' }}
            className="inline-flex flex-col justify-center min-h-[44px] mt-2 focus-ring rounded-sm"
          >
            <span className="text-sm font-semibold text-primary">
              {startHere.kind === 'hub' ? 'Toolkit' : programs.find((program) => program.slug === startHere.slug)?.kind === 'REFERENCE' ? 'Reference' : 'Program'}
            </span>
            <span className="text-lg font-semibold text-gray-900">{startHere.title}</span>
          </PlaceLink>
          {placeMatches.length > 1 && (
            <Link
              to={`/resources?q=${encodeURIComponent(query)}`}
              className="inline-flex items-center min-h-[44px] mt-2 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            >
              More places
            </Link>
          )}
        </Card>
      )}

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:sticky lg:top-32 lg:h-fit">
          <CatalogFacetSidebar
            facets={facets}
            selected={selected}
            programs={programOptions}
            selectedPrograms={selectedPrograms}
            onToggle={handleToggle}
            onToggleProgram={handleToggleProgram}
            onClearFilters={handleClearFilters}
            pending={loading}
          />
        </div>

        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-sm text-gray-600" role="status">
              {loading ? (
                'Loading...'
              ) : (
                <>
                  <span className="font-semibold text-gray-900">{total}</span>{' '}
                  {total === 1 ? 'resource' : 'resources'} found
                </>
              )}
            </p>
            <label htmlFor="resource-catalog-sort" className="inline-flex items-center gap-2 text-sm text-gray-700">
              Sort
              <select
                id="resource-catalog-sort"
                value={sort}
                onChange={(event) => handleSort(event.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white focus:border-primary focus:ring-2 focus:ring-primary"
              >
                {query && <option value="relevance">Relevance</option>}
                <option value="updated">Updated</option>
                <option value="newest">Newest</option>
              </select>
            </label>
          </div>

          {loading && (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <LoadingSkeleton key={index} lines={4} className="h-32" />
              ))}
            </div>
          )}

          {error && (
            <ErrorState
              error={error}
              onRetry={() => window.location.reload()}
            />
          )}

          {!loading && !error && resources.length === 0 && (
            <EmptyState
              title="No resources found"
              description={emptyDescription}
              actionLabel="Clear filters"
              onAction={handleClearFilters}
            />
          )}

          {!loading && !error && resources.length > 0 && (
            <>
              <div className="space-y-4 mb-8">
                {resources.map((resource) => (
                  <CatalogResourceRow
                    key={resource.wpId}
                    resource={resource}
                    places={placesByPost.get(resource.wpId)}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
